// ============================================================================
// PRE-LITIGATION HOOKS (Phase 7)
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { preLitigationService } from '../../api/services/preLitigationService';
import type { ID } from '../../api/types';
import type {
  PreLitCase,
  PreLitCaseListItem,
  PreLitCaseSearchRequest,
  EligibleAccount,
  EligiblePoolSearchRequest,
  ClosedCaseReviewRow,
  ClosedCasesReviewSearchRequest,
  ReceiptRow,
  ReceiptsSearchRequest,
  PreLitClosureReason,
} from '../../api/types/preLitigation.types';

const EMPTY_PAGINATION = {
  currentPage: 1,
  pageSize: 25,
  totalPages: 0,
  totalItems: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

// ----------------------------------------------------------------------------
// POOL
// ----------------------------------------------------------------------------

export const useEligibilityPool = (params?: EligiblePoolSearchRequest) => {
  const [data, setData] = useState<EligibleAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState(EMPTY_PAGINATION);

  const paramsKey = JSON.stringify(params);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await preLitigationService.getPool(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, error, pagination, refetch: fetch };
};

// ----------------------------------------------------------------------------
// CASES — list
// ----------------------------------------------------------------------------

export const usePreLitCases = (params?: PreLitCaseSearchRequest) => {
  const [data, setData] = useState<PreLitCaseListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState(EMPTY_PAGINATION);

  const paramsKey = JSON.stringify(params);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await preLitigationService.getCases(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, error, pagination, refetch: fetch };
};

// ----------------------------------------------------------------------------
// CASE — detail
// ----------------------------------------------------------------------------

export const usePreLitCase = (id: ID | undefined) => {
  const [data, setData] = useState<PreLitCase | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await preLitigationService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, error, refetch: fetch };
};

// ----------------------------------------------------------------------------
// MUTATION HOOKS — claim / assign / log_action / commitment / escalate / close
// Each returns a single function + loading + error.
// ----------------------------------------------------------------------------

const useMutation = <TArgs extends any[], TResult>(
  fn: (...args: TArgs) => Promise<TResult>,
) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const run = async (...args: TArgs): Promise<TResult> => {
    setLoading(true);
    setError(null);
    try {
      return await fn(...args);
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { run, loading, error };
};

export const useClaimCase = () =>
  useMutation((id: ID) => preLitigationService.claim(id));

export const useAssignAgent = () =>
  useMutation((id: ID, req: Parameters<typeof preLitigationService.assign>[1]) =>
    preLitigationService.assign(id, req),
  );

export const useLogAgentAction = () =>
  useMutation((id: ID, req: Parameters<typeof preLitigationService.logAction>[1]) =>
    preLitigationService.logAction(id, req),
  );

export const useCaptureCommitment = () =>
  useMutation(
    (id: ID, req: Parameters<typeof preLitigationService.captureCommitment>[1]) =>
      preLitigationService.captureCommitment(id, req),
  );

export const useResolveCommitment = () =>
  useMutation(
    (id: ID, req: Parameters<typeof preLitigationService.resolveCommitment>[1]) =>
      preLitigationService.resolveCommitment(id, req),
  );

export const useEscalateCase = () =>
  useMutation((id: ID, req: Parameters<typeof preLitigationService.escalate>[1]) =>
    preLitigationService.escalate(id, req),
  );

export const useManualClose = () =>
  useMutation((id: ID, req: Parameters<typeof preLitigationService.manualClose>[1]) =>
    preLitigationService.manualClose(id, req),
  );

export const useClosePreLitCase = () =>
  useMutation((id: ID, req: Parameters<typeof preLitigationService.closeCase>[1]) =>
    preLitigationService.closeCase(id, req),
  );

/** Loader for PRE_LITIGATION-scoped closure reasons for a given case. */
export const usePreLitClosureReasons = (caseId: ID | null | undefined) => {
  const [data, setData] = useState<PreLitClosureReason[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!caseId) { setData([]); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    preLitigationService.getClosureReasons(caseId)
      .then(rows => { if (!cancelled) setData(rows); })
      .catch(err => { if (!cancelled) setError(err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [caseId]);

  return { data, loading, error };
};

export const useRecomputePool = () =>
  useMutation((req: Parameters<typeof preLitigationService.recomputePool>[0]) =>
    preLitigationService.recomputePool(req),
  );

// ----------------------------------------------------------------------------
// Closed Cases Review worksheet
// ----------------------------------------------------------------------------

export const useClosedCases = (params: ClosedCasesReviewSearchRequest) => {
  const [data, setData] = useState<ClosedCaseReviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState(EMPTY_PAGINATION);

  const paramsKey = JSON.stringify(params);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await preLitigationService.getClosedCases(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, error, pagination, refetch: fetch };
};

export const useConfirmClosureReview = () =>
  useMutation(
    (id: ID, req: Parameters<typeof preLitigationService.confirmClosureReview>[1]) =>
      preLitigationService.confirmClosureReview(id, req),
  );

export const useReopenPreLitCase = () =>
  useMutation((id: ID, req: Parameters<typeof preLitigationService.reopen>[1]) =>
    preLitigationService.reopen(id, req),
  );

// ----------------------------------------------------------------------------
// Receipts worksheet
// ----------------------------------------------------------------------------

export const useReceipts = (params: ReceiptsSearchRequest) => {
  const [data, setData] = useState<ReceiptRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState(EMPTY_PAGINATION);

  const paramsKey = JSON.stringify(params);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await preLitigationService.getReceipts(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, error, pagination, refetch: fetch };
};

export const useVerifyReceipt = () =>
  useMutation(
    (commitmentId: ID, req: Parameters<typeof preLitigationService.verifyReceipt>[1]) =>
      preLitigationService.verifyReceipt(commitmentId, req),
  );
